import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Box, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDispatch, useSelector } from 'react-redux';
import {
    deleteComment,
    deleteCommentLike,
    getProjectComments,
    leaveComment,
    likeComment,
} from '../../redux/project/actions';
import type { IProject, TComment } from '../../types/global/helper';
import type { RootState } from '../../redux/root-reducer';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { convertMinuteSecondsDays, getJWtDetails } from '../../utils/AppUtils';
import { resolveSandboxMediaUrl } from '../../utils/sandboxCourseApi';
import { likeActionInProgressSelector } from '../../selectors/selectors';
import { api } from '../../configurations/api';
import { commentUrl } from '../../configurations/api/url';
import { httpRequest } from '../../redux/types';

type Props = {
    project: IProject;
    assignmentBrief?: string;
    onGoToProject?: () => void;
};

type CommentRow = TComment & { total_replies?: number };

function displayName(user: any): string {
    if (!user) return 'Member';
    return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.userName || user.firstName || 'Member';
}

function avatarUrl(user: any): string | undefined {
    return resolveSandboxMediaUrl(user?.profile_picture || user?.photo || user?.avatar) || undefined;
}

async function fetchReplies(projectId: string, parentId: string): Promise<CommentRow[]> {
    const qs = new URLSearchParams({
        category: 'project',
        categoryId: projectId,
        parent_comment: parentId,
        page: '1',
        limit: '20',
    });
    const res = await api(`${commentUrl}/feed?${qs}`, httpRequest.GET, null, 2);
    const rows = res?.data || res?.items || [];
    return Array.isArray(rows) ? rows : [];
}

function CommentCard({
    comment,
    projectId,
    depth = 0,
    onLike,
    onReply,
    onDelete,
    canDeleteComment,
}: {
    comment: CommentRow;
    projectId: string;
    depth?: number;
    onLike: (c: TComment) => void;
    onReply: (c: TComment) => void;
    onDelete: (c: TComment) => void;
    canDeleteComment: (c: TComment) => boolean;
}) {
    const liked = Boolean(comment.is_user_liked || (comment.user_like && comment.user_like.length));
    const likes = Number(comment.total_likes) || 0;
    const replyCount = Number(comment.total_replies) || Number(comment.total_comments) || comment.replies?.length || 0;
    const [menuOpen, setMenuOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [loadingReplies, setLoadingReplies] = useState(false);
    const [loadedReplies, setLoadedReplies] = useState<CommentRow[] | null>(
        comment.replies?.length ? (comment.replies as CommentRow[]) : null,
    );
    const canDelete = canDeleteComment(comment);

    // Keep optimistic replies from redux in sync when parent already has them
    useEffect(() => {
        if (comment.replies?.length) {
            setLoadedReplies(comment.replies as CommentRow[]);
        }
    }, [comment.replies]);

    const toggleReplies = async () => {
        if (expanded) {
            setExpanded(false);
            return;
        }
        setExpanded(true);
        if (loadedReplies || depth >= 1) return;
        if (replyCount <= 0) return;
        setLoadingReplies(true);
        try {
            const rows = await fetchReplies(projectId, comment._id);
            setLoadedReplies(rows);
        } catch {
            setLoadedReplies([]);
        } finally {
            setLoadingReplies(false);
        }
    };

    const visibleReplies = loadedReplies || [];

    return (
        <Box
            sx={{
                p: 1.75,
                borderRadius: 2,
                bgcolor: depth ? OBSIDIAN.surfaceContainerLow : OBSIDIAN.surfaceContainer,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                ml: depth ? 3 : 0,
                border: depth ? `1px solid ${OBSIDIAN.outline}22` : 'none',
                transition: 'background 0.15s',
                '&:hover': { bgcolor: depth ? OBSIDIAN.surfaceContainer : OBSIDIAN.surfaceContainerHigh },
            }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <Avatar
                        src={avatarUrl(comment.user)}
                        sx={{
                            width: 32,
                            height: 32,
                            bgcolor: OBSIDIAN.surfaceContainerHighest,
                            fontSize: 12,
                            flexShrink: 0,
                        }}>
                        {displayName(comment.user).slice(0, 1)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 600,
                                fontSize: 12,
                                color: OBSIDIAN.onSurface,
                            }}>
                            {displayName(comment.user)}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontSize: 10,
                                color: OBSIDIAN.onSurfaceVariant,
                            }}>
                            {convertMinuteSecondsDays(comment.createdAt || comment.created_at)} ago
                        </Typography>
                    </Box>
                </Box>
                {canDelete ? (
                    <Box sx={{ position: 'relative' }}>
                        <IconButton
                            size="small"
                            onClick={() => setMenuOpen(v => !v)}
                            sx={{ color: OBSIDIAN.onSurfaceVariant, p: 0.25 }}>
                            <MoreHorizIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        {menuOpen ? (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: 0,
                                    top: 28,
                                    zIndex: 2,
                                    bgcolor: OBSIDIAN.surfaceContainerHighest,
                                    borderRadius: 1,
                                    border: `1px solid ${OBSIDIAN.outline}44`,
                                    overflow: 'hidden',
                                    minWidth: 100,
                                }}>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        onDelete(comment);
                                    }}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        width: '100%',
                                        border: 0,
                                        bgcolor: 'transparent',
                                        color: '#ff7351',
                                        px: 1.25,
                                        py: 1,
                                        cursor: 'pointer',
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        fontSize: 11,
                                        '&:hover': { bgcolor: OBSIDIAN.surfaceContainerHigh },
                                    }}>
                                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                    Delete
                                </Box>
                            </Box>
                        ) : null}
                    </Box>
                ) : null}
            </Box>

            <Typography
                sx={{
                    fontFamily: OBSIDIAN_BODY_FONT,
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.9)',
                    lineHeight: 1.55,
                    whiteSpace: 'pre-wrap',
                }}>
                {comment.comment}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        component="button"
                        type="button"
                        onClick={() => onLike(comment)}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            border: 0,
                            cursor: 'pointer',
                            px: 1,
                            py: 0.5,
                            borderRadius: 999,
                            bgcolor: OBSIDIAN.surfaceContainerLow,
                            color: liked ? OBSIDIAN.primary : OBSIDIAN.onSurfaceVariant,
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 11,
                            fontWeight: 600,
                            '&:hover': { color: OBSIDIAN.primary },
                        }}>
                        {liked ? <FavoriteIcon sx={{ fontSize: 14 }} /> : <FavoriteBorderIcon sx={{ fontSize: 14 }} />}
                        {likes}
                    </Box>
                    {depth < 1 ? (
                        <Box
                            component="button"
                            type="button"
                            onClick={() => onReply(comment)}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                border: 0,
                                bgcolor: 'transparent',
                                cursor: 'pointer',
                                color: OBSIDIAN.onSurfaceVariant,
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontSize: 11,
                                '&:hover': { color: OBSIDIAN.onSurface },
                            }}>
                            <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
                            Reply
                        </Box>
                    ) : null}
                </Box>
                {depth < 1 && replyCount > 0 ? (
                    <Box
                        component="button"
                        type="button"
                        onClick={() => void toggleReplies()}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.25,
                            border: 0,
                            bgcolor: 'transparent',
                            cursor: 'pointer',
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 10,
                            fontWeight: 600,
                            color: OBSIDIAN.secondary,
                        }}>
                        {expanded ? 'Hide' : `${replyCount}`} {replyCount === 1 ? 'reply' : 'replies'}
                        <ExpandMoreIcon
                            sx={{
                                fontSize: 14,
                                transform: expanded ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.15s',
                            }}
                        />
                    </Box>
                ) : null}
            </Box>

            {expanded ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                    {loadingReplies ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                            <CircularProgress size={18} sx={{ color: OBSIDIAN.primary }} />
                        </Box>
                    ) : visibleReplies.length ? (
                        visibleReplies.map(reply => (
                            <CommentCard
                                key={reply._id}
                                comment={reply}
                                projectId={projectId}
                                depth={depth + 1}
                                onLike={onLike}
                                onReply={onReply}
                                onDelete={onDelete}
                                canDeleteComment={canDeleteComment}
                            />
                        ))
                    ) : (
                        <Typography
                            sx={{ fontSize: 11, color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                            No replies yet
                        </Typography>
                    )}
                </Box>
            ) : null}
        </Box>
    );
}

/** Class discussion — Obsidian cards; likes + on-demand replies. */
export function SandboxLessonDiscussion({ project, assignmentBrief, onGoToProject }: Props) {
    const dispatch = useDispatch();
    const { user_id, isSignedIn } = getJWtDetails();
    const comments = useSelector((s: RootState) => s.project.selectedProjectComments || []) as CommentRow[];
    const loading = useSelector((s: RootState) => Boolean(s.project.gettingProjectComments));
    const likingInProgress = useSelector(likeActionInProgressSelector);
    const [draft, setDraft] = useState('');
    const [replyTo, setReplyTo] = useState<TComment | null>(null);

    useEffect(() => {
        if (!project?._id) return;
        dispatch(getProjectComments({ params: `?project=${project._id}&limit=30` }));
    }, [dispatch, project?._id]);

    const topLevel = useMemo(() => (comments || []).filter(c => !c.parent_comment), [comments]);

    const onLike = useCallback(
        (comment: TComment) => {
            if (!isSignedIn || likingInProgress) return;
            const liked = Boolean(comment.is_user_liked || (comment.user_like && comment.user_like.length));
            if (liked && comment.user_like?.[0]?._id) {
                dispatch(
                    deleteCommentLike({
                        documentId: comment.user_like[0]._id,
                        additionalPayload: comment,
                    }),
                );
                return;
            }
            dispatch(
                likeComment({
                    commentId: comment._id,
                    like_category: 'comment',
                    like_type: 'like',
                    like_origin: 'details',
                }),
            );
        },
        [dispatch, isSignedIn, likingInProgress],
    );

    const onDelete = useCallback(
        (comment: TComment) => {
            if (!comment?._id) return;
            dispatch(deleteComment({ documentId: comment._id, payload: project }));
        },
        [dispatch, project],
    );

    const send = useCallback(() => {
        const text = draft.trim();
        if (!text || !project?._id || !isSignedIn) return;
        dispatch(
            leaveComment({
                comment: text,
                project: project as any,
                commentId: replyTo?._id,
                commentType: 'project',
                isReply: Boolean(replyTo),
                parentComment: replyTo || undefined,
            }),
        );
        setDraft('');
        setReplyTo(null);
    }, [dispatch, draft, isSignedIn, project, replyTo]);

    const canDeleteComment = useCallback(
        (c: TComment) => {
            const author = String((c.user as any)?._id || (c.user as any)?.id || c.created_by || '');
            return Boolean(user_id && author && author === String(user_id));
        },
        [user_id],
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 12, position: 'relative' }}>
            {assignmentBrief ? (
                <Box
                    sx={{
                        p: 1.75,
                        borderRadius: 2,
                        bgcolor: OBSIDIAN.surfaceContainerHigh,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.primary,
                        }}>
                        Pinned Mentor Brief
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 12,
                            color: OBSIDIAN.onSurface,
                            lineHeight: 1.55,
                        }}>
                        {assignmentBrief}
                    </Typography>
                    {onGoToProject ? (
                        <Typography
                            component="button"
                            type="button"
                            onClick={onGoToProject}
                            sx={{
                                alignSelf: 'flex-start',
                                border: 0,
                                bgcolor: 'transparent',
                                p: 0,
                                cursor: 'pointer',
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontSize: 11,
                                fontWeight: 700,
                                color: OBSIDIAN.secondary,
                            }}>
                            Go to Project Submission →
                        </Typography>
                    ) : null}
                </Box>
            ) : null}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 700,
                        fontSize: 12,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: OBSIDIAN.onSurfaceVariant,
                    }}>
                    Discussion
                </Typography>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: OBSIDIAN.outline }} />
                <Typography
                    sx={{ fontFamily: OBSIDIAN_BODY_FONT, fontSize: 12, color: OBSIDIAN.secondary, fontWeight: 600 }}>
                    {topLevel.length} comment{topLevel.length === 1 ? '' : 's'}
                </Typography>
            </Box>

            {loading && !topLevel.length ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} sx={{ color: OBSIDIAN.primary }} />
                </Box>
            ) : topLevel.length === 0 ? (
                <Box sx={{ p: 3, borderRadius: 2, bgcolor: OBSIDIAN.surfaceContainer, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: OBSIDIAN_BODY_FONT, fontSize: 12, color: OBSIDIAN.onSurfaceVariant }}>
                        Be the first to leave a thought on this class.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {topLevel.map(c => (
                        <CommentCard
                            key={c._id}
                            comment={c}
                            projectId={String(project._id)}
                            onLike={onLike}
                            onReply={setReplyTo}
                            onDelete={onDelete}
                            canDeleteComment={canDeleteComment}
                        />
                    ))}
                </Box>
            )}

            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 5,
                    mt: 1,
                    px: 0.5,
                    py: 1.25,
                    bgcolor: 'rgba(19,19,19,0.95)',
                    backdropFilter: 'blur(16px)',
                    borderTop: `1px solid ${OBSIDIAN.outline}33`,
                    boxShadow: '0 -4px 24px rgba(0,0,0,0.45)',
                }}>
                {replyTo ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1,
                            px: 0.5,
                        }}>
                        <Typography sx={{ fontFamily: OBSIDIAN_BODY_FONT, fontSize: 11, color: OBSIDIAN.secondary }}>
                            Replying to {displayName(replyTo.user)}
                        </Typography>
                        <Typography
                            component="button"
                            type="button"
                            onClick={() => setReplyTo(null)}
                            sx={{
                                border: 0,
                                bgcolor: 'transparent',
                                cursor: 'pointer',
                                fontSize: 11,
                                color: OBSIDIAN.onSurfaceVariant,
                                fontFamily: OBSIDIAN_BODY_FONT,
                            }}>
                            Cancel
                        </Typography>
                    </Box>
                ) : null}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 720, mx: 'auto' }}>
                    <TextField
                        fullWidth
                        size="small"
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                        placeholder={isSignedIn ? 'Ask a question or leave a thought…' : 'Sign in to comment'}
                        disabled={!isSignedIn}
                        InputProps={{
                            sx: {
                                bgcolor: OBSIDIAN.surfaceContainerHighest,
                                borderRadius: 999,
                                color: OBSIDIAN.onSurface,
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontSize: 12,
                                px: 0.5,
                                '& fieldset': { border: 'none' },
                            },
                        }}
                    />
                    <IconButton
                        onClick={send}
                        disabled={!isSignedIn || !draft.trim()}
                        sx={{
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                            background: `linear-gradient(135deg, ${OBSIDIAN.primary}, #feb700)`,
                            color: OBSIDIAN.onPrimaryFixed,
                            '&:hover': { filter: 'brightness(1.05)' },
                            '&.Mui-disabled': { opacity: 0.4, color: OBSIDIAN.onPrimaryFixed },
                        }}>
                        <ArrowUpwardIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
}
